import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useFCM, PUSH_CHANGED_EVENT } from "../hooks/useFCM";
import { openAppNotificationSettings } from "../lib/native";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ThemeToggle } from "../components/ThemeToggle";
import { motion } from "framer-motion";
import { LogOut, ChevronRight, TrendingUp, TrendingDown, Award, Bell, BellOff, Camera, Trash2, Users, Copy, Check as CheckIcon } from "lucide-react";
import { GoldSwitch } from "../components/GoldSwitch";
import PushDiagnostics from "../components/PushDiagnostics";
import { useTheme } from "../context/ThemeContext";
import { vipTextColor, vipBadgeBg } from "../lib/vipColors";
import { resolveImageUrl } from "../lib/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VIP_CFG = {
  Diamond: { color: "#B9F2FF", bg: "rgba(13,27,62,0.5)", label: "Diamond" },
  Platinum: { color: "#E8E8E8", bg: "rgba(40,40,40,0.5)", label: "Platinum" },
  Gold: { color: "var(--ang-gold)", bg: "rgba(42,31,10,0.5)", label: "Gold" },
  Silver: { color: "#C0C0C0", bg: "rgba(30,30,30,0.5)", label: "Silver" },
  Standard: { color: "var(--ang-text-muted)", bg: "rgba(20,20,20,0.5)", label: "Standard" },
};
import { useSeo } from "../lib/seo";

export default function Profile() {
  useSeo({ title: "Личный кабинет — ANGIOLINI", noindex: true });
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const fcm = useFCM(user);
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [referral, setReferral] = useState(null);
  const [refCopied, setRefCopied] = useState(false);
  const avatarFileRef = React.useRef(null);
  const [avatarUploading, setAvatarUploading] = React.useState(false);

  // Единая подписка: согласие клиента + системное разрешение устройства.
  // consent === null — ещё загружается.
  const [consent, setConsent] = useState(null);
  const [consentBusy, setConsentBusy] = useState(false);
  // "unsupported" | "blocked" | "off" | "on" — состояние разрешения устройства
  const [pushStatus, setPushStatus] = useState("off");

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const sync = async () => {
      const s = await fcm.status();
      if (alive) setPushStatus(s);
    };
    axios.get(`${API}/me/notification-consent`, { withCredentials: true })
      .then(r => { if (alive) setConsent(!!r.data.given); })
      .catch(() => { if (alive) setConsent(false); });
    sync();
    // Тихая регистрация токена при запуске приходит с задержкой — перечитаем
    const late = setTimeout(sync, 2500);
    const onVisible = () => { if (document.visibilityState === "visible") sync(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(PUSH_CHANGED_EVENT, sync);
    return () => {
      alive = false;
      clearTimeout(late);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(PUSH_CHANGED_EVENT, sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const toggleNotifications = async () => {
    if (consent === null || consentBusy) return;
    const next = !consent;
    setConsentBusy(true);
    try {
      const r = await axios.post(`${API}/me/notification-consent`, { given: next },
        { withCredentials: true });
      setConsent(!!r.data.given);
      // Включили — спрашиваем системное разрешение и регистрируем устройство.
      // Выключили — отписываем это устройство; серверный фильтр гасит остальные.
      if (next) {
        const res = await fcm.enable();
        setPushStatus(res === true ? "on" : res === "denied" ? "blocked" : await fcm.status());
      } else {
        await fcm.disable();
        setPushStatus(await fcm.status());
      }
    } catch {
      setConsent(!next);
    } finally { setConsentBusy(false); }
  };

  const notificationsOn = consent === true;
  const pushHint =
    consent === null ? t.common.loading :
    consentBusy ? t.profile.pushBusy :
    !notificationsOn ? t.profile.pushHintOff :
    pushStatus === "blocked" ? t.profile.pushHintBlocked :
    pushStatus === "bridge" ? t.profile.pushHintBridge :
    pushStatus === "unsupported" ? t.profile.pushHintUnsupported :
    t.profile.pushHintOn;

  useEffect(() => {
    const load = async () => {
      const [b, h, r] = await Promise.allSettled([
        axios.get(`${API}/bonus/balance`, { withCredentials: true }),
        axios.get(`${API}/bonus/history`, { withCredentials: true }),
        axios.get(`${API}/referral/me`, { withCredentials: true }),
      ]);
      if (b.status === "fulfilled") setBalance(b.value.data);
      if (h.status === "fulfilled") setHistory(h.value.data.slice(0, 8));
      if (r.status === "fulfilled" && r.value?.data) setReferral(r.value.data);
    };
    load();
  }, []);

  const vipLevel = balance?.vip_level ?? user?.vip_level ?? "Silver";
  const bonusBalance = balance?.balance ?? balance?.bonus_balance ?? 0;
  const lockedBalance = balance?.locked || 0;
  const expiringSoon = balance?.expiring_soon || 0;
  const totalPurchases = balance?.total_purchases ?? user?.total_purchases ?? 0;
  const { theme } = useTheme();
  const cfgBase = VIP_CFG[vipLevel] || VIP_CFG.Standard;
  const cfg = { ...cfgBase, color: vipTextColor(vipLevel, theme, cfgBase.color), bg: vipBadgeBg(vipLevel, theme, cfgBase.bg) };
  const initials = (user?.name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const referralLink = referral?.code
    ? `${window.location.origin}/auth?ref=${referral.code}`
    : "";

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = referralLink; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setRefCopied(true);
    setTimeout(() => setRefCopied(false), 1800);
  };

  const shareReferral = async () => {
    const text = referral?.share_text || `Присоединяйся к Angiolini VIP клубу: ${referralLink}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Angiolini VIP", text, url: referralLink });
      } else {
        await copyReferralLink();
      }
    } catch { /* user cancelled */ }
  };

  const handleLogout = async () => { await logout(); navigate("/auth"); };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    setDeleteError(""); setDeleting(true);
    try {
      await axios.post(`${API}/me/delete-account`, { confirm: deleteConfirm },
        { withCredentials: true });
      await logout();
      navigate("/auth");
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || "Не удалось удалить аккаунт");
    } finally {
      setDeleting(false);
    }
  };

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/auth/avatar`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshUser();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Не удалось загрузить аватар");
    } finally {
      setAvatarUploading(false);
      if (avatarFileRef.current) avatarFileRef.current.value = "";
    }
  };

  const handleAvatarRemove = async () => {
    try {
      await axios.delete(`${API}/auth/avatar`, { withCredentials: true });
      await refreshUser();
    } catch { /* noop */ }
  };

  return (
    <div style={{ background: "var(--ang-bg)", minHeight: "100vh", paddingBottom: "24px" }}>
      {/* Header / Avatar */}
      <div style={{ padding: "52px 20px 32px", background: "linear-gradient(180deg,var(--ang-bg-top) 0%,var(--ang-bg) 100%)", textAlign: "center" }}>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
          {/* Avatar with upload */}
          <input ref={avatarFileRef} type="file" accept="image/*" style={{ display: "none" }}
            data-testid="avatar-file-input"
            onChange={(e) => handleAvatarUpload(e.target.files?.[0])} />
          <div style={{ position: "relative", width: "92px", margin: "0 auto 16px" }}>
            <button onClick={() => avatarFileRef.current?.click()} disabled={avatarUploading}
              data-testid="profile-avatar"
              style={{
                width: "92px", height: "92px", borderRadius: "50%",
                background: user?.avatar_url
                  ? `url(${resolveImageUrl(user.avatar_url)}) center/cover no-repeat, var(--ang-surface)`
                  : `linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "Cormorant Garamond", fontSize: "34px",
                color: user?.avatar_url ? "transparent" : "var(--ang-bg)",
                fontWeight: 600,
                border: "2px solid var(--ang-gold-line)",
                cursor: avatarUploading ? "wait" : "pointer",
                outline: "none",
                padding: 0,
                position: "relative",
                overflow: "hidden",
              }}>
              {!user?.avatar_url && initials}
              {avatarUploading && (
                <div style={{ position: "absolute", inset: 0, background: "rgba(10,10,10,0.55)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ang-text)", fontFamily: "Manrope", fontSize: "10px" }}>
                  ...
                </div>
              )}
            </button>
            {/* Camera badge */}
            <button onClick={() => avatarFileRef.current?.click()} disabled={avatarUploading}
              data-testid="avatar-edit-btn"
              style={{
                position: "absolute", right: "-2px", bottom: "-2px",
                width: "30px", height: "30px", borderRadius: "50%",
                background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))",
                border: "2px solid var(--ang-bg)",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "var(--ang-bg)", padding: 0,
              }}>
              <Camera size={14} />
            </button>
            {user?.avatar_url && (
              <button onClick={handleAvatarRemove}
                data-testid="avatar-remove-btn"
                title="Удалить фото"
                style={{
                  position: "absolute", left: "-4px", bottom: "-2px",
                  width: "26px", height: "26px", borderRadius: "50%",
                  background: "var(--ang-nav-bg)", border: "1px solid var(--ang-border-strong)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "var(--ang-text-soft)", padding: 0,
                }}>
                <Trash2 size={11} />
              </button>
            )}
          </div>
          <h1 data-testid="profile-name" style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", color: "var(--ang-text)", fontWeight: 400, marginBottom: "6px" }}>{user?.name}</h1>
          <div data-testid="profile-phone" style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-faint)", marginBottom: "12px" }}>{user?.phone}</div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: cfg.bg, border: `1px solid ${cfg.color}44`, borderRadius: "20px", padding: "4px 14px" }}>
            <Award size={14} style={{ color: cfg.color }} />
            <span data-testid="profile-vip-level" style={{ fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: cfg.color, letterSpacing: "0.1em" }}>{vipLevel}</span>
          </div>
        </motion.div>
      </div>

      {/* Stats */}
      <div style={{ padding: "0 16px 16px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <div data-testid="profile-bonus" style={{ background: "var(--ang-surface)", borderRadius: "8px", padding: "16px", textAlign: "center" }}>
            <div style={{ fontFamily: "Cormorant Garamond", fontSize: "28px", color: "var(--ang-gold)", fontWeight: 600 }}>{Math.floor(bonusBalance).toLocaleString("ru-RU")}</div>
            <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: "4px" }}>{t.profile.bonuses}</div>
            {(lockedBalance > 0 || expiringSoon > 0) && (
              <div style={{ marginTop: "8px", borderTop: "1px solid var(--ang-border)", paddingTop: "8px", display: "flex", flexDirection: "column", gap: "3px" }}>
                {lockedBalance > 0 && (
                  <div data-testid="locked-balance" style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-gold-strong)" }}>
                    ⏳ Ждут разблокировки: {Math.floor(lockedBalance).toLocaleString("ru-RU")}
                  </div>
                )}
                {expiringSoon > 0 && (
                  <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "rgba(225,29,42,0.75)" }}>
                    Скоро сгорит: {Math.floor(expiringSoon).toLocaleString("ru-RU")}
                  </div>
                )}
              </div>
            )}
          </div>
          <div data-testid="profile-purchases" style={{ background: "var(--ang-surface)", borderRadius: "8px", padding: "16px", textAlign: "center" }}>
            <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", color: "var(--ang-text)", fontWeight: 600 }}>{(totalPurchases / 1000).toFixed(0)} K</div>
            <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: "4px" }}>{t.profile.purchasesLabel}</div>
          </div>
        </div>
      </div>

      {/* Bonus rule banner: 20% pay limit */}
      <div style={{ padding: "0 16px 16px" }}>
        <div data-testid="bonus-rule-card" style={{ background: "linear-gradient(135deg, var(--ang-gold-soft), rgba(212,184,120,0.04))", border: "1px solid var(--ang-gold-line)", borderRadius: "8px", padding: "12px 14px", display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "var(--ang-gold-soft)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ang-gold)", fontFamily: "Cormorant Garamond", fontSize: "18px", fontWeight: 600, flexShrink: 0 }}>20%</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Cormorant Garamond", fontSize: "16px", color: "var(--ang-text)", fontWeight: 500, marginBottom: "2px" }}>Оплачивайте бонусами</div>
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-muted)", lineHeight: 1.45 }}>
              До 20% стоимости любого товара со скидкой можно оплатить бонусами с этого счёта
            </div>
          </div>
        </div>
      </div>

      {/* Referral block */}
      {referral && (
        <div style={{ padding: "0 16px 16px" }}>
          <div data-testid="referral-card" style={{ background: "linear-gradient(135deg, var(--ang-gold-soft), rgba(20,20,20,0.7))", border: "1px solid var(--ang-gold-line)", borderRadius: "10px", padding: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
              <Users size={18} style={{ color: "var(--ang-gold)" }} />
              <div style={{ fontFamily: "Cormorant Garamond", fontSize: "18px", color: "var(--ang-text)", fontWeight: 500 }}>Пригласите подругу</div>
            </div>
            <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-text-muted)", lineHeight: 1.5, marginBottom: "12px" }}>
              За регистрацию по вашей ссылке вы получите <b style={{ color: "var(--ang-gold-2)" }}>5 000 ₸</b>, а с каждой её покупки — <b style={{ color: "var(--ang-gold-2)" }}>до 1.5% кэшбэка</b>.
            </div>

            <div style={{ display: "flex", gap: "8px", alignItems: "stretch", marginBottom: "12px" }}>
              <div style={{ flex: 1, background: "var(--ang-surface-2)", border: "1px solid var(--ang-border)", borderRadius: "6px", padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Ваш код</div>
                  <div data-testid="referral-code" style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", color: "var(--ang-gold)", fontWeight: 600, letterSpacing: "0.1em" }}>{referral.code}</div>
                </div>
                <button onClick={copyReferralLink} data-testid="copy-referral-btn"
                  style={{ background: "var(--ang-gold-soft)", border: "1px solid var(--ang-gold-line)", borderRadius: "6px", padding: "8px 10px", cursor: "pointer", color: "var(--ang-gold)", display: "flex", alignItems: "center", gap: "4px", fontFamily: "Manrope", fontSize: "11px" }}>
                  {refCopied ? <CheckIcon size={13} /> : <Copy size={13} />}
                  {refCopied ? "Скопировано" : "Копия"}
                </button>
              </div>
            </div>

            <button onClick={shareReferral} data-testid="share-referral-btn"
              style={{ width: "100%", background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", color: "var(--ang-bg)", border: "none", borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Поделиться ссылкой
            </button>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "12px" }}>
              <div style={{ background: "var(--ang-border)", borderRadius: "6px", padding: "10px", textAlign: "center" }}>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", color: "var(--ang-text)", fontWeight: 600 }}>{referral.referrals_count}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "var(--ang-text-faint)", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: "2px" }}>Приглашено</div>
              </div>
              <div style={{ background: "var(--ang-border)", borderRadius: "6px", padding: "10px", textAlign: "center" }}>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", color: "var(--ang-gold)", fontWeight: 600 }}>{Math.floor(referral.earned).toLocaleString("ru-RU")}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "var(--ang-text-faint)", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: "2px" }}>Заработано ₸</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Единая подписка на уведомления ANGIOLINI */}
      <div style={{ padding: "0 16px 12px" }}>
        <div data-testid="push-settings-card"
          style={{ background: notificationsOn ? "var(--ang-gold-soft)" : "var(--ang-surface)",
            border: `1px solid ${notificationsOn ? "var(--ang-gold-line)" : "var(--ang-border)"}`,
            borderRadius: "8px", padding: "14px 16px",
            transition: "background-color .25s ease, border-color .25s ease" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
              <span style={{ color: notificationsOn ? "var(--ang-gold)" : "var(--ang-text-faint)", display: "flex" }}>
                {notificationsOn ? <Bell size={16} /> : <BellOff size={16} />}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text)", fontWeight: 600 }}>
                  {t.profile.pushTitle}
                </div>
                <div data-testid="push-status-label"
                  style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-muted)", marginTop: "2px", lineHeight: 1.35 }}>
                  {pushHint}
                </div>
              </div>
            </div>

            {/* Переключатель: золотой в активном состоянии, как на iPhone */}
            <GoldSwitch testId="enable-push-btn" on={notificationsOn} onClick={toggleNotifications}
              disabled={consent === null || consentBusy} label={t.profile.pushTitle} />
          </div>

          {notificationsOn && pushStatus === "blocked" && (
            <button data-testid="open-push-settings-btn" onClick={openAppNotificationSettings}
              style={{ marginTop: "12px", width: "100%", background: "transparent",
                border: "1px solid var(--ang-gold-line)", borderRadius: "6px", padding: "9px",
                cursor: "pointer", color: "var(--ang-gold)", fontFamily: "Manrope", fontSize: "11px",
                letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>
              {t.profile.pushOpenSettings}
            </button>
          )}
        </div>
      </div>
      {/* Диагностика push (видна только внутри приложения) */}
      <PushDiagnostics />



      {/* Language Switcher */}
      <div style={{ padding: "0 16px 16px" }}>
        <div style={{ background: "var(--ang-surface)", borderRadius: "8px", padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-soft)" }}>{t.profile.language}</span>
          <LanguageSwitcher dark={true} />
        </div>
      </div>

      {/* Оформление: кремовая тема ребрендинга или классическая чёрная */}
      <div style={{ padding: "0 16px 16px" }}>
        <div style={{ background: "var(--ang-surface)", borderRadius: "8px", padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid var(--ang-border)" }}>
          <span style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-soft)" }}>Оформление</span>
          <ThemeToggle />
        </div>
      </div>

      {/* Menu */}
      <div style={{ padding: "0 16px 20px" }}>
        {[
          { label: t.profile.bonusCard, path: "/app/bonus", testid: "menu-bonus" },
          { label: t.profile.catalog, path: "/app/catalog", testid: "menu-catalog" },
          { label: t.profile.news, path: "/app/news", testid: "menu-news" },
          { label: t.profile.appointment, path: "/app/appointment", testid: "menu-appointment" },
        ].map(({ label, path, testid }) => (
          <button key={path} data-testid={testid} onClick={() => navigate(path)}
            style={{ width: "100%", background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "6px", padding: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", marginBottom: "8px", fontFamily: "Manrope", fontSize: "14px", color: "var(--ang-text-soft)" }}>
            {label}
            <ChevronRight size={16} style={{ color: "var(--ang-text-faint)" }} />
          </button>
        ))}
      </div>

      {/* Recent Transactions */}
      {history.length > 0 && (
        <div style={{ padding: "0 16px 20px" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", fontWeight: 400, marginBottom: "12px" }}>{t.profile.recent}</h3>
          {history.map(txn => {
            const positive = (txn.amount || 0) > 0;
            const typeLabels = {
              welcome: "Приветственный",
              cashback: "Кэшбэк",
              tier_cashback: "Кэшбэк",
              purchase_cashback: "Кэшбэк",
              referral_signup: "Реферал",
              referral_purchase: "Кэшбэк с друга",
              referral_inviter: "Реферал",
              referral_invitee: "Бонус другу",
              birthday: "День рождения",
              spend: "Списание",
              spent: "Списание",
              expired: "Истёк",
              admin_adjust: "Корректировка",
              admin_grant: "Начислено админом",
              admin_deduct: "Списано админом",
            };
            const typeLabel = typeLabels[txn.type] || txn.type || "Бонус";
            const isLocked = txn.unlock_at && new Date(txn.unlock_at) > new Date();
            return (
              <div key={txn.id} data-testid={`profile-txn-${txn.id}`} style={{ background: "var(--ang-surface)", borderRadius: "6px", padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
                  {positive
                    ? <TrendingUp size={14} style={{ color: "var(--ang-gold)", flexShrink: 0 }} />
                    : <TrendingDown size={14} style={{ color: "#ef4444", flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      <span style={{ fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-text-soft)", fontWeight: 600 }}>{typeLabel}</span>
                      {isLocked && (
                        <span style={{ background: "var(--ang-gold-soft)", border: "1px solid var(--ang-gold-line)", color: "var(--ang-gold)", borderRadius: "3px", padding: "1px 5px", fontFamily: "Manrope", fontSize: "8.5px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                          Заморожено
                        </span>
                      )}
                    </div>
                    {txn.description && (
                      <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-muted)", marginTop: "1px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {txn.description}
                      </div>
                    )}
                  </div>
                </div>
                <span style={{ fontFamily: "Manrope", fontSize: "13px", fontWeight: 700, color: positive ? "var(--ang-gold)" : "#ef4444", whiteSpace: "nowrap", marginLeft: "8px" }}>
                  {positive ? "+" : ""}{Math.floor(txn.amount || 0).toLocaleString("ru-RU")}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Profile Info */}
      {user?.city && (
        <div style={{ padding: "0 16px 20px" }}>
          <div style={{ background: "var(--ang-surface)", borderRadius: "8px", padding: "16px" }}>
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", marginBottom: "4px" }}>{t.profile.city}</div>
            <div style={{ fontFamily: "Manrope", fontSize: "15px", color: "var(--ang-text)" }}>{user.city}</div>
          </div>
        </div>
      )}

      {/* Logout */}
      <div style={{ padding: "0 16px 16px" }}>
        <button data-testid="logout-btn" onClick={handleLogout}
          style={{ width: "100%", background: "transparent", border: "1px solid var(--ang-border)", borderRadius: "6px", padding: "14px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", cursor: "pointer", fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-faint)" }}>
          <LogOut size={16} />{t.profile.logout}
        </button>
      </div>

      {/* Удаление аккаунта (требование App Store) */}
      <div style={{ padding: "0 16px 16px" }}>
        {!deleteOpen ? (
          <button data-testid="delete-account-open" onClick={() => setDeleteOpen(true)}
            style={{ width: "100%", background: "transparent", border: "1px solid rgba(220,38,38,0.25)",
                      borderRadius: "6px", padding: "13px", cursor: "pointer", fontFamily: "Manrope",
                      fontSize: "12.5px", color: "rgba(248,113,113,0.75)" }}>
            Удалить аккаунт
          </button>
        ) : (
          <div data-testid="delete-account-panel"
            style={{ background: "var(--ang-surface)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: "8px", padding: "16px" }}>
            <div style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "var(--ang-text-soft)", lineHeight: 1.55 }}>
              Аккаунт и личные данные будут удалены навсегда: имя, телефон, фото,
              все примерки и записи на визит. Бонусы и история покупок обезличиваются
              и остаются только в бухгалтерских документах бутика.
              Для подтверждения введите слово <strong style={{ color: "#f87171" }}>УДАЛИТЬ</strong>.
            </div>
            <input data-testid="delete-account-confirm" value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="УДАЛИТЬ"
              style={{ width: "100%", marginTop: "12px", padding: "11px 12px", background: "var(--ang-bg)",
                        border: "1px solid var(--ang-border)", borderRadius: "6px", color: "var(--ang-text)",
                        fontFamily: "Manrope", fontSize: "13px", outline: "none", boxSizing: "border-box" }} />
            {deleteError && (
              <div data-testid="delete-account-error" style={{ fontFamily: "Manrope", fontSize: "12px", color: "#f87171", marginTop: "8px" }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
              <button data-testid="delete-account-cancel"
                onClick={() => { setDeleteOpen(false); setDeleteConfirm(""); setDeleteError(""); }}
                style={{ flex: 1, background: "transparent", border: "1px solid var(--ang-border)",
                          borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope",
                          fontSize: "12.5px", color: "var(--ang-text-muted)" }}>
                Отмена
              </button>
              <button data-testid="delete-account-submit" onClick={handleDeleteAccount} disabled={deleting}
                style={{ flex: 1, background: "rgba(220,38,38,0.85)", border: "none", borderRadius: "6px",
                          padding: "12px", cursor: deleting ? "wait" : "pointer", fontFamily: "Manrope",
                          fontSize: "12.5px", fontWeight: 700, color: "var(--ang-text)", opacity: deleting ? 0.6 : 1 }}>
                {deleting ? "Удаляем…" : "Удалить навсегда"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Legal footer */}
      <div style={{ padding: "0 16px 24px", display: "flex", flexDirection: "column", gap: "4px", alignItems: "center" }}>
        <a href="/legal?tab=contacts" data-testid="profile-link-contacts"
          style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)", textDecoration: "none", padding: "4px" }}>
          Контакты бутика
        </a>
        <a href="/legal?tab=privacy" data-testid="profile-link-privacy"
          style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)", textDecoration: "none", padding: "4px" }}>
          Политика конфиденциальности
        </a>
        <a href="/legal?tab=terms" data-testid="profile-link-terms"
          style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)", textDecoration: "none", padding: "4px" }}>
          Условия программы лояльности
        </a>
        <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-border-strong)", marginTop: "8px", textAlign: "center" }}>
          ТОО «Italy Retail» · БИН 220540021868
        </div>
      </div>
    </div>
  );
}
